import {
  AnswerOptionCondition,
  AnswerOptionConditionType,
  isConditionGroup,
  ConditionNode,
  ConditionGroup,
} from 'shared/types/due-diligence';
import { FILELOG_FIELDS, DATABASE_FIELDS } from './condition-utils';

/**
 * Get Dutch label for a FileLog field
 */
function getFieldLabel(fieldValue: string): string {
  try {
    const field = FILELOG_FIELDS.find((f) => f.value === fieldValue);
    return field?.label || fieldValue;
  } catch (error) {
    console.error('Error getting field label:', error);
    return fieldValue;
  }
}

/**
 * Get Dutch label for a database field
 */
function getDatabaseFieldLabel(fieldKey: string): string {
  try {
    for (const category of DATABASE_FIELDS) {
      const field = category.fields.find((f) => f.key === fieldKey);
      if (field) {
        return field.label;
      }
    }
    return fieldKey;
  } catch (error) {
    console.error('Error getting database field label:', error);
    return fieldKey;
  }
}

/**
 * Get Dutch label for an operator
 */
function getOperatorLabel(operator: string): string {
  const operatorMap: Record<string, string> = {
    '==': 'gelijk aan',
    '!=': 'niet gelijk aan',
    '>': 'groter dan',
    '<': 'kleiner dan',
    '>=': 'groter dan of gelijk aan',
    '<=': 'kleiner dan of gelijk aan',
    contains: 'bevat',
    not_empty: 'niet leeg',
    is_empty: 'leeg',
    equals: 'gelijk aan',
    not_equals: 'niet gelijk aan',
  };

  try {
    return operatorMap[operator] || operator;
  } catch (error) {
    console.error('Error getting operator label:', error);
    return operator;
  }
}

/**
 * Generate a human-readable Dutch sentence for a FILE_EXISTS condition
 */
function getFileExistsLabel(condition: AnswerOptionCondition): string {
  try {
    if (condition.fileExists === true) {
      return 'Als het bestand **bestaat**';
    } else if (condition.fileExists === false) {
      return 'Als het bestand **niet bestaat**';
    }
    return 'Bestand bestaat conditie (niet geconfigureerd)';
  } catch (error) {
    console.error('Error generating FILE_EXISTS label:', error);
    return 'Bestand bestaat conditie (fout)';
  }
}

/**
 * Generate a human-readable Dutch sentence for an EXPIRATION_DATE condition
 */
function getExpirationDateLabel(condition: AnswerOptionCondition): string {
  try {
    const { expirationComparison, expirationDays } = condition;

    if (expirationComparison === 'more_than_days' && expirationDays) {
      return `Als de vervaldatum **meer dan ${expirationDays} dagen geldig** is`;
    } else if (expirationComparison === 'within_days' && expirationDays) {
      return `Als de vervaldatum **binnen ${expirationDays} dagen** verloopt`;
    } else if (expirationComparison === 'expired') {
      return 'Als het document **verlopen** is';
    } else if (expirationComparison === 'not_expired') {
      return 'Als het document **niet verlopen** is';
    }

    return 'Vervaldatum conditie (niet geconfigureerd)';
  } catch (error) {
    console.error('Error generating EXPIRATION_DATE label:', error);
    return 'Vervaldatum conditie (fout)';
  }
}

/**
 * Generate a human-readable Dutch sentence for a FIELD_VALUE condition
 */
function getFieldValueLabel(condition: AnswerOptionCondition): string {
  try {
    const { field, operator, value } = condition;

    if (!field || !operator) {
      return 'Veldwaarde conditie (niet geconfigureerd)';
    }

    const fieldLabel = getFieldLabel(field);

    if (operator === 'not_empty') {
      return `Als **${fieldLabel}** **niet leeg** is`;
    } else if (operator === 'is_empty') {
      return `Als **${fieldLabel}** **leeg** is`;
    } else if (operator === 'contains') {
      return `Als **${fieldLabel}** **bevat** **${value || ''}**`;
    } else {
      const operatorLabel = getOperatorLabel(operator);
      return `Als **${fieldLabel}** **${operatorLabel}** **${value || ''}**`;
    }
  } catch (error) {
    console.error('Error generating FIELD_VALUE label:', error);
    return 'Veldwaarde conditie (fout)';
  }
}

/**
 * Generate a human-readable Dutch sentence for an AI_QUESTION condition
 */
function getAiQuestionLabel(condition: AnswerOptionCondition): string {
  try {
    const { aiQuestion, expectedAnswer } = condition;

    if (!aiQuestion) {
      return 'AI vraag (niet geconfigureerd)';
    }

    const expectedLabel =
      expectedAnswer === 'yes'
        ? 'ja'
        : expectedAnswer === 'no'
          ? 'nee'
          : 'ja/nee';

    return `Als AI beantwoordt '**${aiQuestion}**' met **${expectedLabel}**`;
  } catch (error) {
    console.error('Error generating AI_QUESTION label:', error);
    return 'AI vraag (fout)';
  }
}

/**
 * Generate a human-readable Dutch sentence for an AI_REFERENCE condition
 */
function getAiReferenceLabel(condition: AnswerOptionCondition): string {
  try {
    const { aiReference } = condition;

    if (!aiReference) {
      return 'AI referentie (niet geconfigureerd)';
    }

    return `Als AI '**${aiReference}**' vindt in het document`;
  } catch (error) {
    console.error('Error generating AI_REFERENCE label:', error);
    return 'AI referentie (fout)';
  }
}

/**
 * Generate a human-readable Dutch sentence for a DATABASE_COMPARISON condition
 */
function getDatabaseComparisonLabel(condition: AnswerOptionCondition): string {
  try {
    const {
      documentValueSource,
      documentExtractionPrompt,
      extractionQuestionFieldName,
      databaseField,
    } = condition;

    let extractionLabel = '';

    if (documentValueSource === 'aiExtraction' && documentExtractionPrompt) {
      extractionLabel = documentExtractionPrompt;
    } else if (
      documentValueSource === 'extractionQuestion' &&
      extractionQuestionFieldName
    ) {
      extractionLabel = extractionQuestionFieldName;
    } else {
      return 'Database vergelijking (niet geconfigureerd)';
    }

    const dbFieldLabel = databaseField
      ? getDatabaseFieldLabel(databaseField)
      : 'database veld';

    return `Vergelijk **${extractionLabel}** met **${dbFieldLabel}**`;
  } catch (error) {
    console.error('Error generating DATABASE_COMPARISON label:', error);
    return 'Database vergelijking (fout)';
  }
}

/**
 * Generate a human-readable Dutch sentence for a QUESTION_ANSWER condition
 */
function getQuestionAnswerLabel(condition: AnswerOptionCondition): string {
  try {
    const {
      referencedQuestionId,
      expectedOptionId,
      expectedOptionValue,
      questionAnswerComparison,
    } = condition;

    // In real usage, you would fetch the question name from context
    // For now, we'll use the ID as a placeholder
    const questionName = referencedQuestionId
      ? `Vraag #${referencedQuestionId}`
      : 'vraag';
    const optionLabel = expectedOptionValue || `Optie #${expectedOptionId}`;
    const comparisonLabel = getOperatorLabel(
      questionAnswerComparison || 'equals'
    );

    if (!referencedQuestionId) {
      return 'Vraag antwoord (niet geconfigureerd)';
    }

    return `Als antwoord op '**${questionName}**' **${comparisonLabel}** **${optionLabel}**`;
  } catch (error) {
    console.error('Error generating QUESTION_ANSWER label:', error);
    return 'Vraag antwoord (fout)';
  }
}

/**
 * Generate a human-readable Dutch sentence for a condition
 */
export function getConditionLabel(condition: AnswerOptionCondition): string {
  try {
    switch (condition.type) {
      case AnswerOptionConditionType.FILE_EXISTS:
        return getFileExistsLabel(condition);

      case AnswerOptionConditionType.EXPIRATION_DATE:
        return getExpirationDateLabel(condition);

      case AnswerOptionConditionType.FIELD_VALUE:
        return getFieldValueLabel(condition);

      case AnswerOptionConditionType.AI_QUESTION:
        return getAiQuestionLabel(condition);

      case AnswerOptionConditionType.AI_REFERENCE:
        return getAiReferenceLabel(condition);

      case AnswerOptionConditionType.DATABASE_COMPARISON:
        return getDatabaseComparisonLabel(condition);

      case AnswerOptionConditionType.QUESTION_ANSWER:
        return getQuestionAnswerLabel(condition);

      default:
        console.warn(`Unknown condition type: ${condition.type}`);
        return 'Onbekende conditie';
    }
  } catch (error) {
    console.error('Error generating condition label:', error, condition);
    return 'Conditie (fout)';
  }
}

/**
 * Recursively count conditions in a tree
 */
function countConditionsInTree(node: ConditionNode): number {
  try {
    if (isConditionGroup(node)) {
      return node.children.reduce(
        (sum, child) => sum + countConditionsInTree(child),
        0
      );
    }
    return 1;
  } catch (error) {
    console.error('Error counting conditions in tree:', error);
    return 0;
  }
}

/**
 * Generate a summary label for a full condition tree
 */
export function getConditionTreeSummary(root: ConditionNode | null): string {
  try {
    if (!root) {
      return 'Geen voorwaarden';
    }

    // If it's a single condition (not a group), return its label
    if (!isConditionGroup(root)) {
      return getConditionLabel(root);
    }

    // If it's a group, count conditions and show logic
    const count = countConditionsInTree(root);

    if (count === 0) {
      return 'Geen voorwaarden';
    } else if (count === 1) {
      // If there's only one condition in the group, find it and return its label
      const findFirstCondition = (
        node: ConditionNode
      ): AnswerOptionCondition | null => {
        if (!isConditionGroup(node)) {
          return node;
        }
        for (const child of node.children) {
          const found = findFirstCondition(child);
          if (found) return found;
        }
        return null;
      };

      const firstCondition = findFirstCondition(root);
      return firstCondition
        ? getConditionLabel(firstCondition)
        : 'Geen voorwaarden';
    }

    const logic = (root as ConditionGroup).logic || 'AND';
    return `${count} voorwaarden (${logic})`;
  } catch (error) {
    console.error('Error generating condition tree summary:', error);
    return 'Fout bij laden voorwaarden';
  }
}
